import { useState, useEffect, useRef } from "react";
import {
  LayoutDashboard,
  ShieldCheck,
  Building2,
  UserRound,
  TrendingUp,
  Trophy,
  Coins,
  Target,
  Boxes,
  Users,
} from "lucide-react";

const RADIUS = 30;
const CIRC = 2 * Math.PI * RADIUS;
const SUCCESS_PCT = 0.94;
const REQ_PCT = 0.82;
const BARS = [42, 68, 55, 83, 61, 92, 74]; // weekly run volume (%)

const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* count from 0 → end once `run` is true */
function CountUp({ end, suffix = "", comma = false, run, duration = 1300 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) return;
    if (reduceMotion()) {
      setV(end);
      return;
    }
    let raf;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      setV(Math.round(end * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [run, end, duration]);
  return (
    <>
      {comma ? v.toLocaleString() : v}
      {suffix}
    </>
  );
}

const ROLE_DASHBOARDS = [
  {
    icon: ShieldCheck,
    title: "Dashboard Overview",
    who: "Platform admins",
    metrics: [
      "Companies, universities & individual learners",
      "New account creation — 30-day trend",
      "Infrastructure coverage % · boxes & topologies",
      "Box-request success rate & pending-approval queue",
      "Top-performing companies leaderboard",
    ],
    dir: "lp-left",
  },
  {
    icon: Building2,
    title: "Organization Overview",
    who: "Organization admins",
    metrics: [
      "Total members, teams & owned boxes",
      "Members-by-role & new-member trends",
      "Top teams and top performers",
      "Boxes completed / attempted · active runs",
      "LLM token usage with near-cap alerts",
    ],
    dir: "lp-down",
  },
  {
    icon: UserRound,
    title: "My Dashboard",
    who: "Professionals & Students",
    metrics: [
      "Boxes completed & first-try success rate",
      "Solo progression & attempt history",
      "Difficulty breakdown with average times",
      "Category coverage & recommendations",
      "SCyWF / NIST 800-181 framework alignment",
    ],
    dir: "lp-right",
  },
];

const CARD =
  "rounded-xl border border-(--color-border) bg-(--color-bg-card) p-3";
const HEAD =
  "mb-2 text-[9.5px] font-medium uppercase tracking-wider text-(--color-text-muted)";

function AnalyticsPanel() {
  const wrapRef = useRef(null);
  const vpRef = useRef(null);
  const innerRef = useRef(null);
  const barRef = useRef(null);
  const [live, setLive] = useState(false);

  // start everything when the panel scrolls into view
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setLive(true);
          ob.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  // auto-scroll (ping-pong) once live
  useEffect(() => {
    if (!live || reduceMotion()) return;
    const vp = vpRef.current;
    const inner = innerRef.current;
    if (!vp || !inner) return;
    let pos = 0,
      dir = 1,
      pause = 40,
      hover = false,
      raf,
      max = 0;
    const measure = () => {
      max = Math.max(0, inner.offsetHeight - vp.clientHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(inner);
    const enter = () => (hover = true);
    const leave = () => (hover = false);
    vp.addEventListener("mouseenter", enter);
    vp.addEventListener("mouseleave", leave);
    const SPEED = 1.35; // px/frame — faster scroll
    const loop = () => {
      if (!hover) {
        if (pause > 0) pause--;
        else {
          pos += dir * SPEED;
          if (pos >= max) {
            pos = max;
            dir = -1;
            pause = 60;
          }
          if (pos <= 0) {
            pos = 0;
            dir = 1;
            pause = 60;
          }
        }
        inner.style.transform = `translateY(${-pos}px)`;
        if (barRef.current)
          barRef.current.style.top = (max ? (pos / max) * 74 : 0) + "%";
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      vp.removeEventListener("mouseenter", enter);
      vp.removeEventListener("mouseleave", leave);
    };
  }, [live]);

  // live tick on "active runs"
  const [runs, setRuns] = useState(128);
  useEffect(() => {
    if (!live || reduceMotion()) return;
    const id = setInterval(
      () => setRuns(128 + Math.floor(Math.random() * 7 - 3)),
      2400,
    );
    return () => clearInterval(id);
  }, [live]);

  const draw = (len) => ({
    strokeDasharray: len,
    strokeDashoffset: live ? 0 : len,
    transition: "stroke-dashoffset 1.5s ease .2s",
  });

  return (
    <div
      ref={wrapRef}
      className="dash-shot lp-float-slow relative flex flex-col overflow-hidden rounded-2xl qv-card qv-noise"
      style={{ height: "clamp(470px, 62vh, 560px)" }}
    >
      {/* pinned header */}
      <div className="flex items-center justify-between border-b border-(--color-border) px-5 py-3.5">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="h-4 w-4 text-(--color-primary)" />
          <span className="qv-mono text-[11px] uppercase tracking-wider text-(--color-text-muted)">
            Dashboard Overview
          </span>
        </div>
        <span className="flex items-center gap-1.5">
          <span className="cyber-badge-dot" />
          <span className="qv-mono text-[10px] uppercase text-(--color-primary)">
            live · auto
          </span>
        </span>
      </div>

      {/* scrolling viewport */}
      <div
        ref={vpRef}
        className="relative flex-1 overflow-hidden"
        style={{
          WebkitMaskImage:
            "linear-gradient(180deg,transparent,#000 5%,#000 94%,transparent)",
          maskImage:
            "linear-gradient(180deg,transparent,#000 5%,#000 94%,transparent)",
        }}
      >
        {/* scroll progress thumb */}
        <span className="absolute right-1.5 top-2 bottom-2 z-10 w-0.75 rounded-full bg-(--color-border)">
          <span
            ref={barRef}
            className="absolute left-0 h-[26%] w-full rounded-full bg-[linear-gradient(180deg,var(--color-primary-hover),var(--color-primary))]"
          />
        </span>

        <div
          ref={innerRef}
          className="flex flex-col gap-3 p-4 will-change-transform"
        >
          {/* KPI tiles */}
          <div className="grid grid-cols-3 gap-3">
            {[
              {
                icon: TrendingUp,
                node: <CountUp end={1248} comma run={live} />,
                label: "Total members",
              },
              { icon: Coins, node: runs, label: "Active runs", live: true },
              {
                icon: Target,
                node: <CountUp end={78} suffix="%" run={live} />,
                label: "First-try success",
              },
            ].map((kpi) => {
              const Icon = kpi.icon;
              return (
                <div key={kpi.label} className={CARD}>
                  <Icon className="mb-2 h-3.5 w-3.5 text-(--color-primary)" />
                  <div className="qv-num text-lg text-(--color-text-primary)">
                    {kpi.node}
                  </div>
                  <div className="text-[9.5px] uppercase tracking-wider text-(--color-text-muted)">
                    {kpi.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* weekly run volume + success ring */}
          <div className="grid grid-cols-5 gap-3">
            <div className={`col-span-3 ${CARD}`}>
              <div className={HEAD}>Weekly run volume</div>
              <div className="flex h-20 items-end gap-1.5">
                {BARS.map((h, i) => (
                  <span
                    key={i}
                    className="lp-bar flex-1 rounded-t bg-[linear-gradient(180deg,var(--color-primary),var(--color-primary-hover))]"
                    style={{ height: `${h}%`, transitionDelay: `${i * 70}ms` }}
                  />
                ))}
              </div>
            </div>
            <div
              className={`col-span-2 flex items-center justify-center ${CARD}`}
            >
              <div className="relative h-19 w-19">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 76 76">
                  <circle
                    cx="38"
                    cy="38"
                    r={RADIUS}
                    fill="none"
                    stroke="var(--color-border-solid)"
                    strokeWidth="7"
                  />
                  <circle
                    cx="38"
                    cy="38"
                    r={RADIUS}
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray={CIRC}
                    strokeDashoffset={CIRC}
                    className="lp-ring"
                    style={{ "--lp-ring-to": CIRC * (1 - SUCCESS_PCT) }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="qv-num text-base text-(--color-text-primary)">
                    94%
                  </span>
                  <span className="text-[8px] uppercase tracking-wider text-(--color-text-muted)">
                    success
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* box requests / creation — daily breakdown */}
          <div className={CARD}>
            <div className="mb-1 flex items-center justify-between">
              <div className={HEAD} style={{ marginBottom: 0 }}>
                Box requests / creation — daily
              </div>
              <div className="flex items-center gap-3 qv-mono text-[9px]">
                <span className="flex items-center gap-1 text-(--color-primary)">
                  <span className="h-1.5 w-1.5 rounded-full bg-(--color-primary)" />
                  Completed
                </span>
                <span className="flex items-center gap-1 text-[#e2564b]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#e2564b]" />
                  Failed
                </span>
              </div>
            </div>
            <svg
              viewBox="0 0 320 96"
              preserveAspectRatio="none"
              className="h-24 w-full"
            >
              <defs>
                <linearGradient id="dgok" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="rgba(34,197,94,.28)" />
                  <stop offset="1" stopColor="rgba(34,197,94,0)" />
                </linearGradient>
              </defs>
              <path
                d="M0,90 L28,84 L48,14 L70,84 L120,91 L180,91 L192,70 L204,91 L255,91 L268,46 L280,91 L320,91 L320,96 L0,96 Z"
                fill="url(#dgok)"
                style={{
                  opacity: live ? 1 : 0,
                  transition: "opacity 1s ease .3s",
                }}
              />
              <path
                d="M0,90 L28,84 L48,14 L70,84 L120,91 L180,91 L192,70 L204,91 L255,91 L268,46 L280,91 L320,91"
                fill="none"
                stroke="var(--color-primary)"
                strokeWidth="1.8"
                style={draw(760)}
              />
              <path
                d="M0,91 L28,88 L48,58 L70,88 L120,92 L268,92 L280,86 L320,92"
                fill="none"
                stroke="#e2564b"
                strokeWidth="1.8"
                style={draw(760)}
              />
            </svg>
          </div>

          {/* box requests success donut + legend */}
          <div className={CARD}>
            <div className={HEAD}>Box requests</div>
            <div className="flex items-center gap-4">
              <div className="relative h-21 w-21 shrink-0">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 84 84">
                  <circle
                    cx="42"
                    cy="42"
                    r={RADIUS}
                    fill="none"
                    stroke="var(--color-border-solid)"
                    strokeWidth="8"
                  />
                  <circle
                    cx="42"
                    cy="42"
                    r={RADIUS}
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={CIRC}
                    strokeDashoffset={live ? CIRC * (1 - REQ_PCT) : CIRC}
                    style={{
                      transition: "stroke-dashoffset 1.5s ease .2s",
                      filter: "drop-shadow(0 0 5px rgba(34,197,94,.5))",
                    }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="qv-num text-[15px] text-(--color-primary)">
                    82%
                  </span>
                  <span className="text-[7px] uppercase tracking-wider text-(--color-text-muted)">
                    success
                  </span>
                </div>
              </div>
              <div className="flex-1 space-y-2 text-[12px]">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-(--color-text-muted)" />
                  <span className="text-(--color-text-secondary)">
                    Boxes requested
                  </span>
                  <b className="ml-auto qv-mono text-(--color-text-primary)">
                    <CountUp end={38} run={live} />
                  </b>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-(--color-primary)" />
                  <span className="text-(--color-text-secondary)">
                    Completed successfully
                  </span>
                  <b className="ml-auto qv-mono text-(--color-primary)">
                    <CountUp end={28} run={live} />
                  </b>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#e2564b]" />
                  <span className="text-(--color-text-secondary)">Failed</span>
                  <b className="ml-auto qv-mono text-[#e2564b]">
                    <CountUp end={6} run={live} />
                  </b>
                </div>
              </div>
            </div>
          </div>

          {/* members by role + difficulty solved */}
          <div className="grid grid-cols-2 gap-3">
            <div className={CARD}>
              <div className={`${HEAD} flex items-center gap-1.5`}>
                <Users className="h-3 w-3 text-(--color-primary)" />
                Members by role
              </div>
              <div className="flex items-center gap-3">
                <svg
                  width="72"
                  height="72"
                  viewBox="0 0 72 72"
                  className="-rotate-90 shrink-0"
                >
                  <circle
                    cx="36"
                    cy="36"
                    r="28"
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeWidth="9"
                    strokeDasharray="87.9 175.8"
                  />
                  <circle
                    cx="36"
                    cy="36"
                    r="28"
                    fill="none"
                    stroke="#5b9df0"
                    strokeWidth="9"
                    strokeDasharray="87.9 175.8"
                    strokeDashoffset="-87.9"
                  />
                </svg>
                <div className="flex-1 space-y-2 text-[11.5px]">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-(--color-primary)" />
                    Users
                    <b className="ml-auto qv-mono">2</b>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[#5b9df0]" />
                    Admins
                    <b className="ml-auto qv-mono">2</b>
                  </div>
                </div>
              </div>
            </div>
            <div className={CARD}>
              <div className={HEAD}>Difficulty solved</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  { l: "Easy", n: 6, c: "var(--color-primary)" },
                  { l: "Med", n: 9, c: "#ef9f27" },
                  { l: "Hard", n: 3, c: "#e2564b" },
                ].map((d) => (
                  <div key={d.l}>
                    <div className="qv-num text-xl" style={{ color: d.c }}>
                      <CountUp end={d.n} run={live} />
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-(--color-text-muted)">
                      {d.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* leaderboard */}
          <div className={CARD}>
            <div className={`${HEAD} flex items-center gap-1.5`}>
              <Trophy className="h-3 w-3 text-(--color-primary)" />
              Top performing companies
            </div>
            {[
              { rank: 1, name: "Northgate Security", pct: 92 },
              { rank: 2, name: "Vertex Labs", pct: 81 },
              { rank: 3, name: "Helios Defense", pct: 74 },
            ].map((row) => (
              <div key={row.rank} className="flex items-center gap-2 py-1">
                <span className="qv-mono w-4 text-[10px] text-(--color-primary)">
                  {row.rank}
                </span>
                <span className="w-28 truncate text-[11px] text-(--color-text-secondary)">
                  {row.name}
                </span>
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-(--color-bg)">
                  <span
                    className="block h-full rounded-full bg-[linear-gradient(90deg,var(--color-primary),var(--color-primary-hover))]"
                    style={{
                      width: live ? `${row.pct}%` : 0,
                      transition: "width 1.1s cubic-bezier(.2,.8,.3,1) .3s",
                    }}
                  />
                </span>
                <span className="qv-mono text-[10px] text-(--color-text-muted)">
                  {row.pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboards() {
  return (
    <section id="dashboards" className="relative py-16 lg:py-24">
      <div className="mx-auto max-w-350 px-5 md:px-10">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          {/* copy */}
          <div className="lp-reveal lp-left max-w-xl">
            <div className="mb-4 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-(--color-primary)">
              <span className="h-px w-6 bg-(--color-primary)" />
              Dashboards & Analytics
            </div>
            <h2 className="qv-glitch text-[32px] sm:text-[40px] font-medium leading-tight tracking-tight text-(--color-text-primary)">
              Every role gets the{" "}
              <span className="lp-highlight">numbers that matter</span> to them
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-(--color-text-secondary)">
              Sign in and land on a dashboard built for your role — from
              platform-wide infrastructure health to a single learner's
              first-try success rate. Live KPIs, trend charts, leaderboards, and
              framework coverage, all in one view.
            </p>
            <ul className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3">
              {[
                "Real-time KPI tiles",
                "30-day trend charts",
                "Team & company leaderboards",
                "Success & completion rings",
                "LLM token-usage tracking",
                "NICE / SCyWF framework coverage",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-[13px] text-(--color-text-secondary)"
                >
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-(--color-primary)" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* live analytics visual */}
          <div className="lp-reveal lp-right">
            <AnalyticsPanel />
          </div>
        </div>

        {/* role dashboard cards */}
        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3">
          {ROLE_DASHBOARDS.map((d, i) => {
            const Icon = d.icon;
            return (
              <div
                key={d.title}
                style={{ transitionDelay: `${i * 90}ms` }}
                className={`lp-reveal ${d.dir} qv-card qv-noise group p-6`}
              >
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-(--color-border-solid) bg-(--color-primary-50) text-(--color-primary) transition-all duration-300 group-hover:shadow-[0_0_18px_var(--color-shadow-primary)]">
                    <Icon className="h-5.5 w-5.5" />
                  </span>
                  <div>
                    <h3 className="text-[15px] font-semibold text-(--color-text-primary)">
                      {d.title}
                    </h3>
                    <p className="qv-mono text-[10px] uppercase tracking-wider text-(--color-text-muted)">
                      {d.who}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {d.metrics.map((m) => (
                    <li
                      key={m}
                      className="flex items-start gap-2 text-[12.5px] leading-relaxed text-(--color-text-secondary)"
                    >
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-(--color-primary)" />
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
